import { PartItem } from '@cutr/constants/cutlist';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { usePartsErrorStore } from '@/api/errors';
import {
  updateAutomaticSheetSizeMaterials,
  useGroupById,
} from '@/api/materialsGroup';
import { useActions } from '@/api/store';
import { cleanNumberInput } from '@/utils/input';

import { TableRowBeam } from '../PartTable/TableRowBeam';
import { TableRowPanel } from './TableRowPanel';
import { TableRowSheet } from './TableRowSheet';
import { TableRowStrip } from './TableRowStrip';

const MAX_PARTS = 10000;

export const TableRow = React.memo(({ part }: { part: PartItem }) => {
  const partErrors = usePartsErrorStore(
    useShallow((state) =>
      state.partErrors.filter((error) => error.partItemId === part.id)
    )
  );
  const addPartError = usePartsErrorStore((state) => state.addPartError);
  const removePartErrors = usePartsErrorStore(
    (state) => state.removePartErrors
  );
  const removePartErrorsByField = usePartsErrorStore(
    (state) => state.removePartErrorsByField
  );

  const { setPart, removePart } = useActions();
  const group = useGroupById(part.groupId);
  const currentRow = React.useRef<HTMLTableRowElement>(null);

  const onPartUpdate = React.useCallback(
    (key: keyof PartItem, value: PartItem[typeof key]) => {
      const updatedPart = { ...part };
      // @ts-expect-error this typing is hard :/
      updatedPart[key] = value;
      setPart(updatedPart, group);

      // auto update sheet size when part doesn't fit on current sheet
      if (
        ['lengthMM', 'widthMM', 'grainDirection'].includes(key) &&
        group?.sheetSizeSelection === 'automatic'
      )
        updateAutomaticSheetSizeMaterials(group);
    },
    [part, group, setPart]
  );

  const onPartDelete = React.useCallback(() => {
    removePartErrors(part.id);
    removePart(part);

    if (group?.sheetSizeSelection === 'automatic')
      updateAutomaticSheetSizeMaterials(group);
  }, [part, group, removePartErrors, removePart]);

  const validateNumericInput = React.useCallback(
    (key: keyof PartItem, value: string) => {
      const number = cleanNumberInput(value);

      if (key === 'quantity') {
        removePartErrorsByField(part.id, key);
        if (+number > MAX_PARTS) {
          addPartError(
            part.id,
            key,
            `Part "${part.label}" quantity cannot be larger than ${MAX_PARTS}!`
          );
        }
      }
      return number;
    },
    [part.id, part.label, removePartErrorsByField, addPartError]
  );

  const hasError = partErrors.length > 0;

  const isFieldValid = React.useCallback(
    (key: keyof PartItem) => {
      return partErrors.filter((error) => error.field === key).length === 0;
    },
    [partErrors]
  );

  return (
    <>
      {part.partType === 'panel' && (
        <TableRowPanel
          ref={currentRow}
          part={part}
          hasError={hasError}
          onPartUpdate={onPartUpdate}
          onPartDelete={onPartDelete}
          isFieldValid={isFieldValid}
          validateNumericInput={validateNumericInput}
        ></TableRowPanel>
      )}
      {part.partType === 'sheet' && (
        <TableRowSheet
          part={part}
          hasError={hasError}
          onPartUpdate={onPartUpdate}
          onPartDelete={onPartDelete}
          isFieldValid={isFieldValid}
          validateNumericInput={validateNumericInput}
        ></TableRowSheet>
      )}

      {part.partType === 'strip' && (
        <TableRowStrip
          ref={currentRow}
          part={part}
          hasError={hasError}
          onPartUpdate={onPartUpdate}
          onPartDelete={onPartDelete}
          isFieldValid={isFieldValid}
          validateNumericInput={validateNumericInput}
        ></TableRowStrip>
      )}
      {part.partType === 'beam' && (
        <TableRowBeam
          part={part}
          hasError={hasError}
          onPartUpdate={onPartUpdate}
          onPartDelete={onPartDelete}
          isFieldValid={isFieldValid}
          validateNumericInput={validateNumericInput}
        />
      )}
    </>
  );
});

TableRow.displayName = 'TableRow';
